
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import DesktopNavigation from './header/DesktopNavigation';
import MobileNavigation from './header/MobileNavigation';
import { NotificationSystem } from './notifications/NotificationSystem';
import { GlobalSearchTrigger } from './search/GlobalSearchTrigger';
import { EnhancedUserMenu } from './header/EnhancedUserMenu';
import LanguageSelector from './LanguageSelector';

// v2 ProyectMind Logo SVG
const PMLogoIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="8" fill="#7C3AED"/>
    <rect x="7" y="7" width="8" height="8" rx="2.5" fill="white" fillOpacity="0.9"/>
    <rect x="17" y="7" width="8" height="8" rx="2.5" fill="#F59E0B"/>
    <rect x="7" y="17" width="8" height="8" rx="2.5" fill="#EC4899"/>
    <rect x="17" y="17" width="8" height="8" rx="2.5" fill="white" fillOpacity="0.35"/>
  </svg>
);

export function Header() {
  const { user, signOut } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className="sticky top-0 z-50 w-full border-b"
      style={{
        background: 'rgba(255,252,247,0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottomColor: '#E2E8F0',
      }}
    >
      <div className="container flex h-16 items-center justify-between gap-4">
        {/* Logo */}
        <div className="flex items-center gap-4 lg:gap-6">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <PMLogoIcon />
            <span
              className="font-extrabold hidden sm:inline text-[#0F0E17]"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '1.05rem', letterSpacing: '-0.03em' }}
            >
              ProyectMind
            </span>
          </Link>

          {/* Desktop Navigation */}
          <DesktopNavigation user={user} isActive={isActive} />
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Language Selector */}
          <div
            className="hidden md:flex items-center rounded-full text-xs font-semibold overflow-hidden"
            style={{ background: '#F1F5F9' }}
          >
            <button
              onClick={() => setLanguage('es')}
              className="px-3 py-1.5 rounded-full transition-all"
              style={{
                background: language === 'es' ? 'white' : 'transparent',
                color: language === 'es' ? '#0F0E17' : '#94A3B8',
                boxShadow: language === 'es' ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
              }}
            >
              ES
            </button>
            <button
              onClick={() => setLanguage('en')}
              className="px-3 py-1.5 rounded-full transition-all"
              style={{
                background: language === 'en' ? 'white' : 'transparent',
                color: language === 'en' ? '#0F0E17' : '#94A3B8',
                boxShadow: language === 'en' ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
              }}
            >
              EN
            </button>
          </div>

          {/* Mobile: keep LanguageSelector for simplicity */}
          <div className="md:hidden">
            <LanguageSelector />
          </div>

          {/* Global Search */}
          {user && <GlobalSearchTrigger />}

          {/* Notifications */}
          {user && (
            <div className="hidden md:flex">
              <NotificationSystem />
            </div>
          )}

          {/* User Menu or Auth Buttons */}
          {user ? (
            <div className="hidden md:flex">
              <EnhancedUserMenu user={user} signOut={signOut} />
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/auth"
                className="px-4 py-2 rounded-full text-sm font-semibold border transition-all"
                style={{
                  borderColor: '#E2E8F0',
                  color: '#0F0E17',
                  background: 'transparent',
                }}
              >
                {t('auth.signIn')}
              </Link>
              <Link
                to="/auth"
                className="px-5 py-2 rounded-full text-sm font-bold text-white transition-all"
                style={{
                  background: '#7C3AED',
                  boxShadow: '0 4px 14px rgba(124,58,237,0.35)',
                }}
              >
                Comenzar Gratis
              </Link>
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-xl transition-colors"
            style={{ background: isMobileMenuOpen ? '#F5F3FF' : 'transparent' }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? t('mobile.closeMenu') : t('mobile.openMenu')}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5 text-[#7C3AED]" />
            ) : (
              <Menu className="h-5 w-5 text-[#0F0E17]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <MobileNavigation
          user={user}
          isActive={isActive}
          signOut={signOut}
          closeMenu={closeMobileMenu}
        />
      )}
    </header>
  );
}
